import {WBPopupDirection, WBPopupTrigger} from '@wandb/ui';
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import * as FuzzyMatch from '@wandb/weave/common/util/fuzzyMatch';
import {isInvalidTag} from '@wandb/weave/common/util/tags';
import {Alias, getTagContrastColor, Tag} from '@wandb/weave/components/Tag';
import {ApolloQueryResult} from 'apollo-client';
import * as _ from 'lodash';
import * as React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button, Input} from 'semantic-ui-react';

import * as GQLTypes from '../types/graphql';

export type SaveTags = (tags: string[]) => Promise<ApolloQueryResult<{}>>;

interface TagAddButtonProps {
  tags: GQLTypes.Tag[];
  availableTags: GQLTypes.Tag[];
  compact?: boolean;
  direction?: WBPopupDirection;
  noun?: 'tag' | 'alias';
  iconOnly?: boolean;
  showColor?: boolean;
  addTag(tag: GQLTypes.Tag): Promise<void>;
}

export const TagAddButton: React.FC<TagAddButtonProps> = ({
  addTag,
  availableTags,
  compact,
  direction,
  noun,
  iconOnly,
}) => {
  const [searchString, setSearchString] = React.useState('');
  const [isAdding, setAdding] = React.useState(false);
  let tagOptions = _.chain(availableTags)
    // .difference(this.props.chosenTags)
    .sort()
    .value();
  tagOptions = FuzzyMatch.fuzzyMatchWithMapping(
    tagOptions,
    searchString,
    t => t.name
  );
  noun = noun ?? 'tag';
  const nextColorIndex = availableTags.length;

  const onTagAdd = async (tag: GQLTypes.Tag, closer: () => void) => {
    setAdding(true);
    await addTag(tag);
    closer();
    setAdding(false);
  };

  return (
    <WBPopupTrigger
      popupContent={({close}) => (
        <div className="tag-add-button-menu">
          <Input
            onClick={(e: any) => e.stopPropagation()}
            icon="search"
            className="search tag-add-button-search"
            placeholder={`Search or create a new ${noun}`}
            value={searchString}
            loading={isAdding}
            onChange={(e, {value}) => {
              if (noun === 'tag' && isInvalidTag(value)) {
                return;
              }
              setSearchString(value);
            }}
            onKeyPress={async (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' && searchString !== '') {
                await onTagAdd(
                  {
                    name: searchString.trim(),
                    colorIndex: nextColorIndex,
                    icon: noun === 'tag' ? 'tag' : undefined,
                  },
                  close
                );
              }
            }}
          />
          <div className="tag-add-button-results">
            {tagOptions.map((tag, index) => (
              <div
                className="tag-add-button-result-item"
                data-test="tag-result"
                key={index}
                onClick={async () => {
                  await onTagAdd(tag, close);
                }}>
                {noun === 'tag' && (
                  <Tag
                    label={tag.name}
                    color={getTagContrastColor(tag.colorIndex)}
                  />
                )}
                {noun === 'alias' && (
                  <Alias
                    label={tag.name}
                    color={getTagContrastColor(tag.colorIndex)}
                  />
                )}
              </div>
            ))}
            {searchString &&
              !availableTags.some(tag => {
                return tag.name.toLowerCase() === searchString.toLowerCase();
              }) && (
                <div
                  key="__create-new"
                  className="tag-add-button-result-item"
                  style={{display: 'flex'}}
                  data-test="create-new-tag"
                  onClick={async () => {
                    await onTagAdd(
                      {
                        name: searchString,
                        colorIndex: nextColorIndex,
                      },
                      close
                    );
                  }}>
                  <span style={{marginRight: '4px'}}>Create new {noun}: </span>
                  {/* Display the color of the tag that will appear in the cell */}
                  {noun === 'tag' && (
                    <Tag
                      label={searchString}
                      color={getTagContrastColor(nextColorIndex)}
                    />
                  )}
                  {noun === 'alias' && (
                    <Alias
                      label={searchString}
                      color={getTagContrastColor(nextColorIndex)}
                    />
                  )}
                </div>
              )}
          </div>
        </div>
      )}
      direction={direction || 'center left'}>
      {({anchorRef, setOpen}) =>
        iconOnly ? (
          <div
            className="tag-add-button-icon-only"
            data-test="tag-add-button"
            onClick={() => setOpen(true)}
            ref={anchorRef}>
            +
          </div>
        ) : (
          <div
            className={`tag-add-button dropdown-menu-text-icon-left-button${
              compact ? ' compact' : ''
            }`}
            data-test="tag-add-button"
            onClick={() => setOpen(true)}
            ref={anchorRef}>
            {compact ? (
              <Button className="wb-icon-button" size="tiny">
                +
              </Button>
            ) : (
              <Button
                size="tiny"
                className="wb-icon-button"
                icon={<LegacyWBIcon name="tag-gray" />}
                content="Add a tag"
              />
            )}
          </div>
        )
      }
    </WBPopupTrigger>
  );
};
